import { AlertCircle, Clock } from 'lucide-react';

export function isLineInTerminationProcess(status: string): boolean {
  return [
    'scheduleTerminationInProgress',
    'scheduledTermination',
    'cancelScheduledTerminationInProgress',
    'terminationInProgress',
  ].includes(status);
}

export function canCancelTermination(status: string): boolean {
  return status === 'scheduledTermination';
}

export function isLineActionsDisabled(status: string): boolean {
  return (
    [
      'terminated',
      'cancelled',
      'rejected',
      'restricted',
      'suspended',
      'settingInProgress',
      'suspensionInProgress',
      'reactivationInProgress',
      'restrictionInProgress',
      'swapSimInProgress',
    ].includes(status) || isLineInTerminationProcess(status)
  );
}

export function isToggleSuspendDisabled(status: string): boolean {
  if (status === 'suspended') return false;
  return isLineActionsDisabled(status);
}

export function getStatusStyle(status: string): {
  bg: string;
  dot: string;
} {
  if (['terminated', 'cancelled', 'rejected'].includes(status))
    return { bg: 'bg-red-50 text-red-700', dot: 'bg-red-400' };
  if (
    [
      'suspended',
      'suspensionInProgress',
      'cancelScheduledTerminationInProgress',
    ].includes(status)
  )
    return { bg: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' };
  if (['restricted', 'restrictionInProgress'].includes(status))
    return { bg: 'bg-orange-50 text-orange-700', dot: 'bg-orange-400' };
  if (
    ['settingInProgress', 'reactivationInProgress', 'swapSimInProgress'].includes(
      status,
    )
  )
    return { bg: 'bg-sky-50 text-sky-700', dot: 'bg-sky-400' };
  if (isLineInTerminationProcess(status))
    return { bg: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' };
  return { bg: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-400' };
}

export function getStatusAlertConfig(status: string) {
  if (status === 'terminated')
    return {
      icon: AlertCircle,
      title: 'Ligne résiliée',
      description:
        "Cette ligne a été résiliée. Aucune modification n'est possible.",
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800',
      descColor: 'text-red-600',
    };
  if (status === 'cancelled')
    return {
      icon: AlertCircle,
      title: 'Ligne annulée',
      description:
        "Cette ligne a été annulée. Aucune modification n'est possible.",
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800',
      descColor: 'text-red-600',
    };
  if (status === 'rejected')
    return {
      icon: AlertCircle,
      title: 'Ligne rejetée',
      description:
        "Cette ligne a été rejetée. Aucune modification n'est possible.",
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800',
      descColor: 'text-red-600',
    };
  if (status === 'restricted')
    return {
      icon: AlertCircle,
      title: 'Ligne restreinte',
      description:
        "Cette ligne est restreinte. Aucune modification n'est possible.",
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      titleColor: 'text-orange-800',
      descColor: 'text-orange-600',
    };
  if (status === 'suspensionInProgress')
    return {
      icon: Clock,
      title: 'Ligne en cours de suspension',
      description:
        "Cette ligne est en cours de suspension. Aucune modification n'est possible.",
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      titleColor: 'text-amber-800',
      descColor: 'text-amber-600',
    };
  if (status === 'reactivationInProgress')
    return {
      icon: Clock,
      title: 'Ligne en cours de réactivation',
      description:
        "Cette ligne est en cours de réactivation. Aucune modification n'est possible.",
      bg: 'bg-sky-50',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      titleColor: 'text-sky-800',
      descColor: 'text-sky-600',
    };
  if (status === 'restrictionInProgress')
    return {
      icon: Clock,
      title: 'Ligne en cours de restriction',
      description:
        "Cette ligne est en cours de restriction. Aucune modification n'est possible.",
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      titleColor: 'text-orange-800',
      descColor: 'text-orange-600',
    };
  if (status === 'swapSimInProgress')
    return {
      icon: Clock,
      title: 'Changement de carte SIM en cours',
      description:
        "Le changement de carte SIM est en cours de traitement. Aucune modification n'est possible.",
      bg: 'bg-sky-50',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      titleColor: 'text-sky-800',
      descColor: 'text-sky-600',
    };
  if (status === 'settingInProgress')
    return {
      icon: Clock,
      title: 'Paramétrage en cours',
      description:
        "Cette ligne est en cours de paramétrage. Aucune modification n'est possible.",
      bg: 'bg-sky-50',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      titleColor: 'text-sky-800',
      descColor: 'text-sky-600',
    };
  if (isLineInTerminationProcess(status))
    return {
      icon: Clock,
      title:
        status === 'scheduleTerminationInProgress' ||
        status === 'terminationInProgress'
          ? 'Résiliation demandée'
          : status === 'scheduledTermination'
            ? 'Résiliation programmée'
            : 'Annulation de la résiliation en cours',
      description:
        status === 'scheduleTerminationInProgress' ||
        status === 'terminationInProgress'
          ? 'La résiliation de cette ligne est en cours de traitement. Les modifications ne sont plus possibles.'
          : status === 'scheduledTermination'
            ? 'La résiliation de cette ligne est programmée. Les modifications ne sont plus possibles.'
            : "L'annulation de la résiliation est en cours de traitement.",
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      titleColor: 'text-amber-800',
      descColor: 'text-amber-600',
    };
  return null;
}
